import React, { useState, useEffect, useRef } from "react";
import type { Lesson } from "@/data/lessons";
import { LESSONS } from "@/data/lessons";
import { useTypingEngine } from "@/hooks/useTypingEngine";
import { useAtom } from "jotai";
import { isLessonUnlocked, progressAtom } from "@/lib/storage";
import Keyboard from "@/components/Keyboard";
import TypingArea from "@/components/TypingArea";
import StatsBar from "@/components/StatsBar";
import GrowingPlant from "./GrowingPlant";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface TypingSessionProps {
  lesson: Lesson;
  onComplete: (wpm: number, accuracy: number) => void;
  onBack: () => void;
  onSwitchLesson: (lessonId: number) => void;
}

export default function TypingSession({ lesson, onComplete, onBack, onSwitchLesson }: TypingSessionProps) {
  const [snippetIndex, setSnippetIndex] = useState(0);
  const snippet = lesson.snippets[snippetIndex]!;
  const { state, wpm, accuracy, currentChar, reset } = useTypingEngine(snippet.code);

  // Track cumulative stats across all snippets
  const cumulativeWpm = useRef<number[]>([]);
  const cumulativeAccuracy = useRef<number[]>([]);

  // Feedback flash state
  const [pressedCorrect, setPressedCorrect] = useState<string | null>(null);
  const [pressedWrong, setPressedWrong] = useState<string | null>(null);
  const prevCursorPos = useRef(0);
  const prevErrors = useRef(0);

  useEffect(() => {
    if (state.cursorPos > prevCursorPos.current) {
      const char = snippet.code[state.cursorPos - 1] ?? "";
      setPressedCorrect(char);
      const t = setTimeout(() => setPressedCorrect(null), 150);
      prevCursorPos.current = state.cursorPos;
      return () => clearTimeout(t);
    }
  }, [state.cursorPos, snippet.code]);

  useEffect(() => {
    if (state.errors > prevErrors.current) {
      setPressedWrong(currentChar);
      const t = setTimeout(() => setPressedWrong(null), 150);
      prevErrors.current = state.errors;
      return () => clearTimeout(t);
    }
  }, [state.errors, currentChar]);

  // Reset refs when snippet changes
  useEffect(() => {
    prevCursorPos.current = 0;
    prevErrors.current = 0;
  }, [snippetIndex]);

  function handleNextSnippet() {
    cumulativeWpm.current.push(wpm);
    cumulativeAccuracy.current.push(accuracy);

    if (snippetIndex < lesson.snippets.length - 1) {
      setSnippetIndex(snippetIndex + 1);
    } else {
      // Lesson complete — average the stats
      const avgWpm = Math.round(
        cumulativeWpm.current.reduce((a, b) => a + b, 0) / cumulativeWpm.current.length,
      );
      const avgAcc = Math.round(
        cumulativeAccuracy.current.reduce((a, b) => a + b, 0) / cumulativeAccuracy.current.length,
      );
      onComplete(avgWpm, avgAcc);
    }
  }

  const [progress] = useAtom(progressAtom);

  return (
    <div className="flex flex-col items-center gap-6 w-full max-w-4xl mx-auto p-4">
      {/* Lesson tabs */}
      <div className="flex gap-1 flex-wrap w-full">
        {LESSONS.map((l) => {
          const done = progress?.completedLessons.includes(l.id) ?? false;
          const isCurrent = l.id === lesson.id;
          const unlocked = progress ? isLessonUnlocked(l.id, progress) : l.id === 1;

          return (
            <button
              key={l.id}
              onClick={() => unlocked && !isCurrent && onSwitchLesson(l.id)}
              disabled={!unlocked}
              className={cn(
                "px-3 py-1.5 rounded-t-md text-sm font-bold transition-all border border-b-0",
                isCurrent && "bg-primary text-primary-foreground",
                !isCurrent && done && "bg-blue-500 text-white hover:bg-blue-600",
                !isCurrent && !done && unlocked && "bg-red-500/80 text-white hover:bg-red-600",
                !isCurrent && !done && !unlocked && "bg-red-500/30 text-red-300 cursor-not-allowed",
              )}
            >
              {l.id}
            </button>
          );
        })}
      </div>

      <StatsBar
        wpm={wpm}
        accuracy={accuracy}
        snippetIndex={snippetIndex}
        totalSnippets={lesson.snippets.length}
        lessonTitle={lesson.title}
      />

      <div className="flex items-start gap-6 w-full">
        <TypingArea snippet={snippet.code} state={state} title={snippet.title} />
        <GrowingPlant percent={snippet.code.length > 0 ? (state.cursorPos / snippet.code.length) * 100 : 0} />
      </div>

      {state.isComplete && (
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            Back to Lessons
          </Button>
          <Button onClick={handleNextSnippet}>
            {snippetIndex < lesson.snippets.length - 1 ? "Next Snippet" : "Complete Lesson"}
          </Button>
        </div>
      )}

      {!state.isComplete && (
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onBack}>
          Back to Lessons
        </Button>
      )}

      <Keyboard
        lessonKeys={lesson.allKeys}
        activeKey={currentChar}
        pressedCorrect={pressedCorrect}
        pressedWrong={pressedWrong}
      />
    </div>
  );
}
